/**
 * Paper Trading Trade Book & Order Book Table Row Models.
 *
 * Formats table rows for the paper terminal's Trade Book (closed positions & P&L)
 * and Order Book (pending, executed, cancelled orders).
 */

import { formatCurrencyInr, formatNumberInr } from "./format";

export const TRADE_CLOSE_REASONS: Record<string, string> = {
  MANUAL: "Manual",
  MTM_LOSS_LIMIT: "MTM Loss Limit",
  MTM_TARGET: "MTM Target",
  INTRADAY_SQUAREOFF: "EOD Square-off",
};

export interface TradeHistoryRecord {
  time: string;
  symbol: string;
  side: string;
  qty: number;
  entry: number;
  exit: number;
  pnl: number;
  charges?: {
    total: number;
    [key: string]: any;
  } | null;
  closeReason?: string;
  [key: string]: any;
}

export interface TradeBookRowModel {
  time: string;
  symbol: string;
  side: string;
  qty: number;
  entryFormatted: string;
  exitFormatted: string;
  grossPnl: number;
  grossPnlFormatted: string;
  grossPnlClass: string;
  chargesFormatted: string;
  netPnl: number;
  netPnlFormatted: string;
  netPnlClass: string;
  reasonLabel: string;
}

export interface PaperOrderRecord {
  id: string;
  time: string;
  timeISO?: string;
  symbol: string;
  orderType: string;
  side: string;
  qty: number;
  triggerPrice?: number | null;
  price?: number | null;
  limitPrice?: number | null;
  product?: string;
  status: string;
  [key: string]: any;
}

export interface PaperOrderBookRowModel {
  id: string;
  time: string;
  symbol: string;
  orderType: string;
  side: string;
  qty: number;
  triggerPriceFormatted: string;
  priceHtml: string;
  product: string;
  status: string;
  statusClass: string;
  isPending: boolean;
}

/**
 * Builds view models for paper trading trade book rows.
 */
export function buildTradeBookRowModels(
  trades: TradeHistoryRecord[],
  netPnlFn: (trade: TradeHistoryRecord) => number,
): TradeBookRowModel[] {
  return (trades || []).map(h => {
    const net = netPnlFn(h);
    const chargesTotal = h.charges ? h.charges.total : 0;
    const reasonLabel =
      (h.closeReason && TRADE_CLOSE_REASONS[h.closeReason]) || "--";
    const grossPnl = h.pnl || 0;
    const grossSign = grossPnl >= 0 ? "+" : "";
    const netSign = net >= 0 ? "+" : "";

    return {
      time: h.time || "",
      symbol: h.symbol || "",
      side: h.side || "",
      qty: h.qty || 0,
      entryFormatted: formatNumberInr(h.entry, 2, "--"),
      exitFormatted: formatNumberInr(h.exit, 2, "--"),
      grossPnl,
      grossPnlFormatted: grossSign + formatCurrencyInr(grossPnl),
      grossPnlClass: grossPnl >= 0 ? "positive" : "negative",
      chargesFormatted: h.charges ? formatCurrencyInr(chargesTotal) : "--",
      netPnl: net,
      netPnlFormatted: netSign + formatCurrencyInr(net),
      netPnlClass: net >= 0 ? "positive" : "negative",
      reasonLabel,
    };
  });
}

/**
 * Builds view models for paper trading order book rows.
 */
export function buildPaperOrderBookRowModels(
  orders: PaperOrderRecord[],
): PaperOrderBookRowModel[] {
  return (orders || []).map(o => {
    const statusClass =
      o.status === "COMPLETE"
        ? "positive"
        : o.status === "CANCELLED"
          ? "negative"
          : "";

    let priceHtml = "--";
    if (o.status === "COMPLETE" && o.price != null) {
      const formattedPrice = formatNumberInr(o.price, 2, "--");
      const hasLimitDiff =
        o.limitPrice != null && Math.abs(o.price - o.limitPrice) > 0.001;
      priceHtml = hasLimitDiff
        ? `${formattedPrice} <span style="color:var(--muted);">(limit ${formatNumberInr(o.limitPrice, 2, "--")})</span>`
        : formattedPrice;
    } else if (o.limitPrice != null) {
      priceHtml = formatNumberInr(o.limitPrice, 2, "--");
    } else if (o.price != null) {
      priceHtml = formatNumberInr(o.price, 2, "--");
    }

    return {
      id: o.id,
      time: o.time || "",
      symbol: o.symbol || "",
      orderType: o.orderType || "",
      side: o.side || "",
      qty: o.qty || 0,
      triggerPriceFormatted:
        o.triggerPrice != null
          ? formatNumberInr(o.triggerPrice, 2, "--")
          : "--",
      priceHtml,
      product: o.product || "MIS",
      status: o.status || "",
      statusClass,
      isPending: o.status === "PENDING",
    };
  });
}
